import React from "react";
import { makeStyles } from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import List from "@civicplus/preamble-ui/lib/List";
import EventListRow from "./EventListRow";
import { EventMeeting } from "../../types/types.event";
import Button from "@civicplus/preamble-ui/lib/Button";
import { makeFullUrl, removeStringDateTimeZone } from "../../utils";
import { Link as RouterLink } from "react-router-dom";
import EventListRowSecondaryActions from "./EventListRowSecondaryActions";
import NoEventMessage from "./NoEventMessage";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { PortalCustomization } from "../../types/types.customization";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
    "& .prmbl-listItemText": {
      margin: 0,
    },
    "& .prmbl-listSubheader": {
      background: theme.palette.background.default,
      marginLeft: -2,
      width: "calc(100% + 4px)",
    },
    "& .cpp-MuiListItem-container": {
      display: (props: PortalCustomization) =>
        props.enablePublicSpeakerSignup ? "flex" : undefined,
      flexDirection: (props: PortalCustomization) =>
        props.enablePublicSpeakerSignup ? "row" : undefined,
    },
  },
  listItem: {
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5, 0, 0, 0.5),
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[1],
    padding: "0 !important",
    textTransform: "none",
    letterSpacing: "normal",
  },
  legacyListItem: {
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[1],
    padding: "0 !important",
    textTransform: "none",
    letterSpacing: "normal",
  },
  errorMessage: {
    marginTop: theme.spacing(4),
    textAlign: "center",
  },
  secondaryAction: {
    top: "auto",
    transform: "none",
    display: "flex",
    right: 0,
    marginBottom: theme.spacing(2),
    position: "relative",
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(0, 0.5, 0.5, 0),
  },
  legacySecondaryActions: {
    top: "auto",
    bottom: theme.spacing(1.25),
    transform: "none",
    display: "flex",
    [theme.breakpoints.down("xs")]: {
      bottom: theme.spacing(0.75),
    },
  },
}));

interface EventListTableProps {
  events: EventMeeting[] | null;
  loading?: boolean;
}

const EventListTable: React.FC<EventListTableProps> = ({ events, loading }) => {
  const portalCustomization = useSelector(
    (state: RootState) => state.portalCustomization
  );
  const classes = useStyles(portalCustomization as PortalCustomization);
  const today = new Date();

  // Sort the events into the relevant headers
  const renderedData: {
    [key: string]: Map<string, EventMeeting>;
  } = {
    "Past Events": new Map(),
    Today: new Map(),
    "Coming Up": new Map(),
  };

  React.useEffect(() => {
    /**
     * Focus management for the event list
     * - PAGE_UP move up by 1
     * - PAGE_DOWN move down by 1
     * - HOME move to top
     * - END move to bottom
     */

    const list = document.getElementById("scroll-wrap");
    if (list) {
      const listItems = list.querySelectorAll(
        "#startScreen, li>*[role='button'], #endScreen"
      );
      const firstItem = listItems[0];
      const lastItem = listItems[listItems.length - 1];

      const handleKeyDown = (e: KeyboardEvent) => {
        const activeElement = document.activeElement;
        if (
          activeElement?.tagName === "A" ||
          activeElement?.tagName === "BUTTON"
        ) {
          const activeIndex = Array.from(listItems).indexOf(activeElement);
          switch (e.key) {
            case "PageUp":
              e.preventDefault();
              if (activeIndex > 0) {
                (listItems[activeIndex - 1] as HTMLElement).focus();
              }
              break;
            case "PageDown":
              e.preventDefault();
              if (activeIndex < listItems.length - 1) {
                (listItems[activeIndex + 1] as HTMLElement).focus();
              }
              break;
            case "Home":
              e.preventDefault();
              (firstItem as HTMLElement).focus();
              break;
            case "End":
              e.preventDefault();
              (lastItem as HTMLElement).focus();
              break;
          }
        }
      };

      list.addEventListener("keydown", handleKeyDown);

      return () => {
        list.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [events]);

  if (events === null || loading) {
    return (
      <List
        id="Event-list"
        className={classes.root}
        aria-label="Loading events by date"
        listItems={Array(10).fill({
          className: portalCustomization?.enablePublicSpeakerSignup
            ? classes.legacyListItem
            : classes.listItem,
          itemText: {
            primary: <EventListRow loading={true} />,
          },
          ListItemSecondaryActionProps: {
            className: portalCustomization?.enablePublicSpeakerSignup
              ? classes.secondaryAction
              : classes.legacySecondaryActions,
          },
          itemSecondaryAction: <EventListRowSecondaryActions loading={true} />,
        })}
      />
    );
  }

  if (events.length === 0) {
    return <NoEventMessage />;
  }

  Array.from(events)
    .sort((a, b) => {
      const dateComparison =
        new Date(a.startDateTime).getTime() -
        new Date(b.startDateTime).getTime();

      // If the dates are equal, then sort by createdOn date
      if (dateComparison === 0) {
        return (
          new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
        );
      }

      return dateComparison;
    })
    .forEach(event => {
      const startDate = new Date(removeStringDateTimeZone(event.startDateTime));
      const renderedId = event.id.toString();
      if (
        startDate.getFullYear() === today.getFullYear() &&
        startDate.getMonth() === today.getMonth() &&
        startDate.getDate() === today.getDate()
      ) {
        renderedData.Today.set(renderedId, event);
      } else {
        if (startDate < today) {
          renderedData["Past Events"].set(renderedId, event);
        } else if (startDate > today) {
          renderedData["Coming Up"].set(renderedId, event);
        }
      }
    });

  // Don't show empty sections
  Object.keys(renderedData).forEach(key => {
    if (renderedData[key].size === 0) {
      delete renderedData[key];
    }
  });

  if (Object.keys(renderedData).length === 0) {
    return <NoEventMessage />;
  }

  const filesPublicPortalPage = "files";
  const overviewPublicPortalPage = "overview";
  const videoPublicPortalPage = "video";

  const defaultPortalPage = (event: EventMeeting) => {
    return event.defaultPublicPortalPage === 0
      ? filesPublicPortalPage
      : event.defaultPublicPortalPage === 1 && !event.cpMediaOnly
      ? overviewPublicPortalPage
      : event.defaultPublicPortalPage === 9
      ? videoPublicPortalPage
      : !event.cpMediaOnly
      ? overviewPublicPortalPage
      : videoPublicPortalPage;
  };

  return (
    <List
      id="Event-list"
      aria-busy={loading ? true : undefined}
      className={classes.root}
      aria-label="Events by date"
      subheaders={Object.keys(renderedData).map((header, i) => {
        const renderedListItems: React.ComponentProps<
          typeof List
        >["listItems"] = [];
        renderedData[header].forEach(thisEvent => {
          // If the component is embedded, use anchor element/href, else use RouterLink/to
          const renderedButtonProps: Partial<
            React.ComponentProps<typeof Button>
          > = {};
          if (process.env.REACT_APP_EMBED) {
            renderedButtonProps.href = makeFullUrl(
              `/event/${thisEvent.id}/${defaultPortalPage(thisEvent)}`
            );
            renderedButtonProps.openInNewWindow = true;
          } else {
            renderedButtonProps.component = RouterLink;
            renderedButtonProps.to = `/event/${
              thisEvent.id
            }/${defaultPortalPage(thisEvent)}`;
          }
          renderedListItems.push({
            button: true,
            role: "button",
            key: thisEvent.id,
            id: thisEvent.id.toString(),
            ...renderedButtonProps,
            // This works, but the type doesn't allow it normally
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            "data-date": thisEvent.eventDate,
            "aria-labelledby": `eventListRow-${thisEvent.id}-title`,
            "aria-describedby": `eventListRow-${thisEvent.id}-datetime`,
            "data-id": thisEvent.id,
            className: portalCustomization?.enablePublicSpeakerSignup
              ? classes.legacyListItem
              : classes.listItem,
            itemText: {
              primary: <EventListRow {...thisEvent} />,
            },
            ListItemSecondaryActionProps: {
              className: portalCustomization?.enablePublicSpeakerSignup
                ? classes.secondaryAction
                : classes.legacySecondaryActions,
            },
            itemSecondaryAction: (
              <EventListRowSecondaryActions {...thisEvent} />
            ),
          });
        });
        return {
          children: header,
          listItems: renderedListItems,
          "aria-hidden": true,
          ListProps: {
            "aria-labelledby": `Event-list-listSubheader-${i}`,
          },
        };
      })}
    />
  );
};

export default EventListTable;
